import { throttle } from 'lodash'
import { useEffect, useRef, useCallback } from 'react'

const CHANNEL_NAME = 'idle_sync'

export const useIdleManager = ({
  idleTime,
  onIdle,
  onActive,
  onLogout,
  onTick,
}) => {
  const channelRef = useRef(null)
  const timerRef = useRef(null)
  const isIdleRef = useRef(false)

  const onIdleRef = useRef(onIdle)
  const onActiveRef = useRef(onActive)
  const onLogoutRef = useRef(onLogout)
  const onTickRef = useRef(onTick)

  useEffect(() => {
    onIdleRef.current = onIdle
  }, [onIdle])

  useEffect(() => {
    onActiveRef.current = onActive
  }, [onActive])

  useEffect(() => {
    onLogoutRef.current = onLogout
  }, [onLogout])

  useEffect(() => {
    onTickRef.current = onTick
  }, [onTick])

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      isIdleRef.current = true
      onIdleRef.current?.()

      channelRef.current?.postMessage({ type: 'IDLE_START' })
    }, idleTime * 1000)
  }, [idleTime])

  const resetTimerRef = useRef(resetTimer)

  useEffect(() => {
    resetTimerRef.current = resetTimer
  }, [resetTimer])

  useEffect(() => {
    channelRef.current = new BroadcastChannel(CHANNEL_NAME)
    channelRef.current.onmessage = (e) => {
      switch (e.data.type) {
        case 'USER_ACTIVE':
        case 'STAY':
          isIdleRef.current = false
          onActiveRef.current?.()
          resetTimerRef.current?.()
          break

        case 'IDLE_START':
          isIdleRef.current = true
          onIdleRef.current?.()
          break

        case 'TICK':
          onTickRef.current?.(e.data.remaining)
          break

        case 'LOGOUT':
          onLogoutRef.current?.()
          break

        default:
          break
      }
    }

    return () => channelRef.current?.close()
  }, [])

  const throttledActivityRef = useRef(
    throttle(() => {
      try {
        if (isIdleRef.current) return
        channelRef?.current?.postMessage({ type: 'USER_ACTIVE' })
        resetTimerRef.current?.()
      } catch (error) {
        console.log('error', error)
      }
    }, 1000)
  )

  useEffect(() => {
    const handler = throttledActivityRef.current
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach((event) => window.addEventListener(event, handler))
    resetTimer()

    return () => {
      events.forEach((event) => window.removeEventListener(event, handler))
      clearTimeout(timerRef.current)
    }
  }, [])

  const broadcastTick = useCallback((remaining) => {
    channelRef.current?.postMessage({ type: 'TICK', remaining })
  }, [])

  const broadcastStay = useCallback(() => {
    isIdleRef.current = false
    channelRef.current?.postMessage({ type: 'STAY' })
    resetTimer()
  }, [resetTimer])

  const broadcastLogout = useCallback(() => {
    channelRef.current?.postMessage({ type: 'LOGOUT' })
  }, [])

  return { broadcastStay, broadcastLogout, broadcastTick, resetTimer }
}

